import React, { useState, useRef, useEffect } from "react";
import "animate.css";
import { Link, useLocation, useNavigate } from "react-router-dom";

export function Header(props) {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isNewsDropdownOpen, setIsNewsDropdownOpen] = useState(false);
  const [isPoliciesDropdownOpen, setIsPoliciesDropdownOpen] = useState(false);
  const [selectedNewsOption, setSelectedNewsOption] = useState("News"); // Default selected option
  const [selectedPoliciesOption, setSelectedPoliciesOption] =
    useState("Policies"); // Default selected option
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const newsDropdownRef = useRef(null);
  const policiesDropdownRef = useRef(null);

  const toggleDonateModal = () => {
    setIsDonateModalOpen(!isDonateModalOpen);
  };

  const toggleMobileNav = () => {
    setIsMobileNavOpen(!isMobileNavOpen);
  };

  const toggleNewsDropdown = () => {
    setIsNewsDropdownOpen(!isNewsDropdownOpen);
    if (!isNewsDropdownOpen) {
      setIsPoliciesDropdownOpen(false);
    }
  };

  const togglePoliciesDropdown = () => {
    setIsPoliciesDropdownOpen(!isPoliciesDropdownOpen);
    if (!isPoliciesDropdownOpen) {
      setIsNewsDropdownOpen(false);
    }
  };

  // Close the mobile nav when navigating
  const closeMobileNav = () => {
    setIsMobileNavOpen(false);
  };

  // Handle news option selection
  const handleNewsOptionSelect = (option, path) => {
    setSelectedNewsOption(option);
    setIsNewsDropdownOpen(false);
    navigate(path);
  };

  // Handle policies option selection
  const handlePoliciesOptionSelect = (option, path) => {
    setSelectedPoliciesOption(option);
    setIsPoliciesDropdownOpen(false);
    navigate(path);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        newsDropdownRef.current &&
        !newsDropdownRef.current.contains(event.target)
      ) {
        setIsNewsDropdownOpen(false);
      }
      if (
        policiesDropdownRef.current &&
        !policiesDropdownRef.current.contains(event.target)
      ) {
        setIsPoliciesDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const location = useLocation();
  const navigate = useNavigate();

  // Update selected options based on current path
  useEffect(() => {
    if (location.pathname === "/blogs") {
      setSelectedNewsOption("Blogs");
    } else if (location.pathname === "/news") {
      setSelectedNewsOption("News");
    } else if (location.pathname === "/reports") {
      setSelectedNewsOption("Reports & Publications");
    } else if (location.pathname === "/awards") {
      setSelectedNewsOption("Awards");
    } else if (location.pathname === "/poshpolicy") {
      setSelectedPoliciesOption("POSH Policy");
    } else if (location.pathname === "/csppolicy") {
      setSelectedPoliciesOption("Child Safeguarding and Protection Policy");
    }
  }, [location.pathname]);

  const scrollToSection = (id) => {
    closeMobileNav(); // Close mobile nav when scrolling to section

    if (location.pathname === "/") {
      // If already on the home page, just scroll smoothly
      const section = document.getElementById(id);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // Navigate to home first, then scroll after navigation
      navigate("/", { replace: false });
      setTimeout(() => {
        const section = document.getElementById(id);
        if (section) {
          section.scrollIntoView({ behavior: "smooth" });
        }
      }, 100); // Small delay to ensure navigation happens first
    }
  };

  return (
    <>
      <nav className="bg-[#efc94c] text-white py-1 px-6 shadow-md">
        <div className="container mx-auto flex justify-between items-center">
          {/* <!-- Logo --> */}
          <div className="flex items-center">
            <img
              className="animate__animated animate__fadeInDown h-15 mr-4"
              src="/images/slumsoccerLogo.jpg"
              alt="SlumSoccer Logo"
            />
          </div>

          {/* <!-- Hamburger Menu Button (Visible on Mobile) --> */}
          <button
            id="hamburger-btn"
            onClick={toggleMobileNav}
            className="md:hidden focus:outline-none"
          >
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16m-7 6h7"
              ></path>
            </svg>
          </button>

          {/* <!-- Desktop Navigation Links (Hidden on Mobile) --> */}
          <ul
            id="nav-links"
            className="hidden md:flex items-center space-x-6 text-lg"
          >
            <li>
              <Link
                to="/"
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                Home
              </Link>
            </li>
            <li>
              <button
                onClick={() => scrollToSection("about")}
                className="hover:text-gray-200 transition text-1.8xl"
              >
                About
              </button>
            </li>
            <li>
              <Link
                to="/projects"
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                Projects
              </Link>
            </li>
            <li>
              <button
                onClick={() => scrollToSection("impact")}
                className="hover:text-gray-200 transition text-1.8xl"
              >
                Impact
              </button>
            </li>
            <li>
              <Link
                to="/gallery"
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                Gallery
              </Link>
            </li>
            <li>
              <Link
                to="/partners"
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                Partners
              </Link>
            </li>
            <li>
              <Link
                to="/fundraisers"
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                Fundraisers
              </Link>
            </li>

            <li className="relative" ref={newsDropdownRef}>
              <button
                onClick={toggleNewsDropdown}
                className="hover:text-gray-200 transition text-1.8xl flex items-center"
              >
                {selectedNewsOption}
                <svg
                  className={`ml-1 w-4 h-4 transition-transform ${
                    isNewsDropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {isNewsDropdownOpen && (
                <div className="absolute z-10 left-0 mt-2 w-56 bg-white rounded-lg shadow-xl py-2 border border-gray-200 animate__animated animate__fadeIn">
                  <button
                    onClick={() => handleNewsOptionSelect("Blogs", "/blogs")}
                    className={`block w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors ${
                      selectedNewsOption === "Blogs"
                        ? "bg-gray-50 text-[#FF6600] font-medium"
                        : "text-gray-800"
                    }`}
                  >
                    Blogs
                  </button>
                  <button
                    onClick={() => handleNewsOptionSelect("News", "/news")}
                    className={`block w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors ${
                      selectedNewsOption === "News"
                        ? "bg-gray-50 text-[#FF6600] font-medium"
                        : "text-gray-800"
                    }`}
                  >
                    News
                  </button>
                  <button
                    onClick={() => handleNewsOptionSelect("Awards", "/awards")}
                    className={`block w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors ${
                      selectedNewsOption === "Awards"
                        ? "bg-gray-50 text-[#FF6600] font-medium"
                        : "text-gray-800"
                    }`}
                  >
                    Awards
                  </button>
                  <button
                    onClick={() =>
                      handleNewsOptionSelect(
                        "Reports & Publications",
                        "/reports"
                      )
                    }
                    className={`block w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors ${
                      selectedNewsOption === "Reports & Publications"
                        ? "bg-gray-50 text-[#FF6600] font-medium"
                        : "text-gray-800"
                    }`}
                  >
                    Reports & Publications
                  </button>
                </div>
              )}
            </li>

            <li className="relative" ref={policiesDropdownRef}>
              <button
                onClick={togglePoliciesDropdown}
                className="hover:text-gray-200 transition text-1.8xl flex items-center"
              >
                Policies
                <svg
                  className={`ml-1 w-4 h-4 transition-transform ${
                    isPoliciesDropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {isPoliciesDropdownOpen && (
                <div className="absolute z-10 left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 border border-gray-200 animate__animated animate__fadeIn">
                  <button
                    onClick={() =>
                      handlePoliciesOptionSelect("POSH Policy", "/poshpolicy")
                    }
                    className={`block w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors ${
                      selectedPoliciesOption === "POSH Policy"
                        ? "bg-gray-50 text-[#FF6600] font-medium"
                        : "text-gray-800"
                    }`}
                  >
                    POSH Policy
                  </button>
                  <button
                    onClick={() =>
                      handlePoliciesOptionSelect(
                        "Child Safeguarding and Protection Policy",
                        "/csppolicy"
                      )
                    }
                    className={`block w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors ${
                      selectedPoliciesOption ===
                      "Child Safeguarding and Protection Policy"
                        ? "bg-gray-50 text-[#FF6600] font-medium"
                        : "text-gray-800"
                    }`}
                  >
                    Child Safeguarding and Protection Policy
                  </button>
                </div>
              )}
            </li>
            <li>
              <Link
                to="/career"
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                Career
              </Link>
            </li>
            <li>
              <Link
                to="/contact"
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                Contact Us
              </Link>
            </li>
            <li>
              <button
                onClick={toggleDonateModal}
                className="bg-white text-[#FF6600] px-4 py-2 text-lg rounded-full hover:bg-gray-200 transition"
              >
                Donate
              </button>
            </li>
          </ul>
        </div>

        {/* <!-- Mobile Navigation Links (Hidden by Default) --> */}
        <div
          id="mobile-nav"
          className={`${isMobileNavOpen ? "block" : "hidden"} md:hidden mt-4`}
        >
          <ul className="space-y-4 text-lg">
            <li>
              <Link
                to="/"
                onClick={closeMobileNav}
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                Home
              </Link>
            </li>
            <li>
              <button
                onClick={() => scrollToSection("about")}
                className="hover:text-gray-200 transition text-1.8xl"
              >
                About
              </button>
            </li>
            <li>
              <Link
                to="/projects"
                onClick={closeMobileNav}
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                Projects
              </Link>
            </li>
            <li>
              <button
                onClick={() => scrollToSection("impact")}
                className="hover:text-gray-200 transition text-1.8xl"
              >
                Impact
              </button>
            </li>
            <li>
              <Link
                to="/gallery"
                onClick={closeMobileNav}
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                Gallery
              </Link>
            </li>
            <li>
              <Link
                to="/partners"
                onClick={closeMobileNav}
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                Partners
              </Link>
            </li>
            <li>
              <Link
                to="/fundraisers"
                onClick={closeMobileNav}
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                Fundraisers
              </Link>
            </li>

            <li>
              <Link
                to="/blogs"
                onClick={closeMobileNav}
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                Blogs
              </Link>
            </li>
            <li>
              <Link
                to="/news"
                onClick={closeMobileNav}
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                News
              </Link>
            </li>
            <li>
              <Link
                to="/awards"
                onClick={closeMobileNav}
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                Awards
              </Link>
            </li>
            <li>
              <Link
                to="/reports"
                onClick={closeMobileNav}
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                Reports & Publications
              </Link>
            </li>

            {/* Policies links in mobile menu */}
            <li>
              <Link
                to="/poshpolicy"
                onClick={closeMobileNav}
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                POSH Policy
              </Link>
            </li>
            <li>
              <Link
                to="/csppolicy"
                onClick={closeMobileNav}
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                Child Safeguarding and Protection Policy
              </Link>
            </li>
            <li>
              <Link
                to="/career"
                onClick={closeMobileNav}
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                Career
              </Link>
            </li>
            <li>
              <Link
                to="/contact"
                onClick={closeMobileNav}
                className="hover:text-gray-200 transition text-1.8xl animate__animated animate__fadeInRightBig"
              >
                Contact Us
              </Link>
            </li>
            <li className="mb-3">
              <button
                onClick={toggleDonateModal}
                className="bg-white text-[#FF6600] px-4 py-2 text-lg rounded-full hover:bg-gray-200 transition"
              >
                Donate
              </button>
            </li>
          </ul>
        </div>
      </nav>

      {/* Donate Modal */}
      {isDonateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl max-w-md w-full mx-4 animate__animated animate__zoomIn border border-gray-100">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#efc94c] to-[#FF6600] rounded-full flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-[#efc94c] to-[#FF6600] bg-clip-text text-transparent">
                    Choose Payment Method
                  </h3>
                </div>
                <button
                  onClick={toggleDonateModal}
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-2 transition-all duration-200"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => {
                    setIsDonateModalOpen(false);
                    setIsQRModalOpen(true);
                  }}
                  className="w-full bg-gradient-to-r from-[#efc94c] to-[#e8b942] text-white py-4 px-6 rounded-xl hover:from-[#e8b942] to-[#d4a73a] transition-all duration-300 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center justify-center space-x-3"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1zm12 0h2a1 1 0 001-1V6a1 1 0 00-1-1h-2a1 1 0 00-1 1v1a1 1 0 001 1zM5 20h2a1 1 0 001-1v-1a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1z"
                    />
                  </svg>
                  <span>Pay using QR Code</span>
                </button>

                <button
                  onClick={() => {
                    setIsDonateModalOpen(false);
                    window.open("https://razorpay.me/@slumsoccer", "_blank");
                  }}
                  className="w-full bg-gradient-to-r from-[#FF6600] to-[#e55a00] text-white py-4 px-6 rounded-xl hover:from-[#e55a00] to-[#cc4f00] transition-all duration-300 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center justify-center space-x-3"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    />
                  </svg>
                  <span>Pay using Payment Gateway</span>
                </button>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-center text-sm text-gray-500">
                  Your donation helps us make a difference in the community
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {isQRModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl max-w-sm w-full mx-4 animate__animated animate__bounceIn border border-gray-100">
            <div className="p-8 text-center">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#efc94c] to-[#FF6600] rounded-full flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1zm12 0h2a1 1 0 001-1V6a1 1 0 00-1-1h-2a1 1 0 00-1 1v1a1 1 0 001 1zM5 20h2a1 1 0 001-1v-1a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold bg-gradient-to-r from-[#efc94c] to-[#FF6600] bg-clip-text text-transparent">
                    Scan QR Code
                  </h3>
                </div>
                <button
                  onClick={() => setIsQRModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-2 transition-all duration-200"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="mb-6 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#efc94c] to-[#FF6600] rounded-2xl opacity-10"></div>
                <div className="relative p-4 bg-white rounded-xl shadow-inner border-2 border-dashed border-gray-200">
                  <img
                    src="/images/QrCode.jpg"
                    alt="Donation QR Code"
                    className="w-full max-w-xs mx-auto rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                  />
                </div>
              </div>

              <div className="mb-6">
                <p className="text-gray-700 text-base font-medium mb-2">
                  Scan with any UPI app to donate
                </p>
                <div className="flex justify-center space-x-4 mb-4">
                  <div className="flex items-center space-x-2 bg-gradient-to-r from-purple-100 to-purple-50 px-3 py-2 rounded-full">
                    <span className="text-purple-600 font-semibold text-sm">
                      GPay
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 bg-gradient-to-r from-blue-100 to-blue-50 px-3 py-2 rounded-full">
                    <span className="text-blue-600 font-semibold text-sm">
                      PhonePe
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 bg-gradient-to-r from-green-100 to-green-50 px-3 py-2 rounded-full">
                    <span className="text-green-600 font-semibold text-sm">
                      Paytm
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setIsQRModalOpen(false)}
                  className="flex-1 bg-gradient-to-r from-[#efc94c] to-[#e8b942] text-white px-6 py-3 rounded-xl hover:from-[#e8b942] to-[#d4a73a] transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Done
                </button>
                <button
                  onClick={() => {
                    setIsQRModalOpen(false);
                    setIsDonateModalOpen(true);
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-200 transition-all duration-300 font-semibold"
                >
                  Back
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
